import { z } from "zod";
import type { WidgetInstance } from "@/models/dashboard/Dashboard";

export const DASHBOARD_SKELETON_CACHE_STORAGE_KEY =
  "lara-law:dashboard-skeleton-layout";

/** Bumped when the JSON envelope or widget shape changes; older versions fail Zod parse and are ignored. */
const CACHE_VERSION = 2 as const;

/** Drop snapshots older than this so layout drift does not linger indefinitely. */
const MAX_ENTRY_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Cap stored dashboards per scope to reduce quota risk; LRU by `updatedAt`. */
const MAX_CACHED_DASHBOARDS = 25;

const positionSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
});

/** Per-widget layout for skeleton cache (v2: id, type, position only — v1 optional `name` removed). */
const layoutWidgetSchema = z.object({
  id: z.string(),
  type: z.string(),
  position: positionSchema,
});

export type DashboardSkeletonLayoutWidget = z.infer<typeof layoutWidgetSchema>;

const scopeSchema = z.object({
  userId: z.string(),
  tenantId: z.string(),
});

const dashboardEntrySchema = z.object({
  updatedAt: z.number(),
  widgets: z.array(layoutWidgetSchema),
});

const fullCacheSchema = z.object({
  version: z.literal(CACHE_VERSION),
  scope: scopeSchema,
  dashboards: z.record(z.string(), dashboardEntrySchema),
});

export type DashboardSkeletonCacheScope = z.infer<typeof scopeSchema>;

function pruneDashboardEntries(
  dashboards: Record<string, z.infer<typeof dashboardEntrySchema>>
): Record<string, z.infer<typeof dashboardEntrySchema>> {
  const now = Date.now();
  const fresh = Object.entries(dashboards).filter(
    ([, v]) => now - v.updatedAt <= MAX_ENTRY_AGE_MS
  );
  if (fresh.length <= MAX_CACHED_DASHBOARDS) {
    return Object.fromEntries(fresh);
  }
  const sorted = [...fresh].sort((a, b) => b[1].updatedAt - a[1].updatedAt);
  return Object.fromEntries(sorted.slice(0, MAX_CACHED_DASHBOARDS));
}

/**
 * Drops TTL-stale rows and applies LRU, then persists when normalized content changes
 * (see {@link maintainDashboardSkeletonCache}).
 */
function rewriteStorageIfDashboardsPruned(
  doc: z.infer<typeof fullCacheSchema>
): z.infer<typeof fullCacheSchema> {
  const prunedDashboards = pruneDashboardEntries(doc.dashboards);
  if (!shouldRewriteAfterPrune(doc.dashboards, prunedDashboards)) {
    return doc;
  }
  try {
    const next: z.infer<typeof fullCacheSchema> = {
      ...doc,
      dashboards: prunedDashboards,
    };
    localStorage.setItem(
      DASHBOARD_SKELETON_CACHE_STORAGE_KEY,
      JSON.stringify(next)
    );
    return next;
  } catch {
    return { ...doc, dashboards: prunedDashboards };
  }
}

function toLayoutWidgets(
  widgets: WidgetInstance[]
): DashboardSkeletonLayoutWidget[] {
  return widgets.map((w) => ({
    id: w.id,
    type: w.type,
    position: {
      x: w.position.x,
      y: w.position.y,
      w: w.position.w,
      h: w.position.h,
    },
  }));
}

/** Canonical string for layout equality (id, type, position). */
function skeletonLayoutContentSignature(
  widgets: DashboardSkeletonLayoutWidget[]
): string {
  return JSON.stringify(
    widgets.map((w) => ({
      id: w.id,
      type: w.type,
      position: w.position,
    }))
  );
}

/** Ignores object key order so key re-ordering alone does not force a localStorage write. */
function normalizedDashboardsFingerprint(
  dashboards: Record<string, z.infer<typeof dashboardEntrySchema>>
): string {
  const rows = Object.entries(dashboards)
    .map(
      ([id, v]) =>
        [id, v.updatedAt, skeletonLayoutContentSignature(v.widgets)] as const
    )
    .sort((a, b) => a[0].localeCompare(b[0]));
  return JSON.stringify(rows);
}

function shouldRewriteAfterPrune(
  before: Record<string, z.infer<typeof dashboardEntrySchema>>,
  after: Record<string, z.infer<typeof dashboardEntrySchema>>
): boolean {
  return (
    normalizedDashboardsFingerprint(before) !==
    normalizedDashboardsFingerprint(after)
  );
}

/**
 * Stable signature for `localConfig.widgets` — use with `useMemo` deps to avoid churn.
 * Matches the payload compared inside {@link persistDashboardSkeletonLayout}.
 */
export function skeletonLayoutSignatureFromInstances(
  widgets: WidgetInstance[]
): string {
  return skeletonLayoutContentSignature(toLayoutWidgets(widgets));
}

/**
 * Inverse of {@link skeletonLayoutSignatureFromInstances} — parses the JSON signature
 * string into validated layout rows for persistence (single source of truth vs. closure).
 */
export function parseSkeletonLayoutSignature(
  signature: string
): DashboardSkeletonLayoutWidget[] | null {
  try {
    const raw: unknown = JSON.parse(signature);
    const parsed = z.array(layoutWidgetSchema).safeParse(raw);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Merges a layout snapshot for one dashboard into localStorage.
 * Swaps the whole document when scope (user/tenant) no longer matches.
 * No-op when the serialized layout matches the existing cache entry (avoids redundant writes on hydration).
 */
export function persistDashboardSkeletonLayout(
  scope: DashboardSkeletonCacheScope,
  dashboardId: string,
  widgets: DashboardSkeletonLayoutWidget[]
): void {
  try {
    const minimized = widgets;
    const raw = localStorage.getItem(DASHBOARD_SKELETON_CACHE_STORAGE_KEY);
    const parsed = raw
      ? fullCacheSchema.safeParse(JSON.parse(raw))
      : null;

    const existingWidgets =
      parsed?.success &&
      parsed.data.scope.userId === scope.userId &&
      parsed.data.scope.tenantId === scope.tenantId
        ? (() => {
            const pruned = pruneDashboardEntries(parsed.data.dashboards);
            const entry = pruned[dashboardId];
            if (!entry) return null;
            if (Date.now() - entry.updatedAt > MAX_ENTRY_AGE_MS) {
              return null;
            }
            return entry.widgets;
          })()
        : null;

    if (
      existingWidgets !== null &&
      skeletonLayoutContentSignature(existingWidgets) ===
        skeletonLayoutContentSignature(minimized)
    ) {
      return;
    }

    let next: z.infer<typeof fullCacheSchema>;

    if (
      parsed?.success &&
      parsed.data.scope.userId === scope.userId &&
      parsed.data.scope.tenantId === scope.tenantId
    ) {
      next = {
        ...parsed.data,
        dashboards: pruneDashboardEntries({
          ...parsed.data.dashboards,
          [dashboardId]: {
            updatedAt: Date.now(),
            widgets: minimized,
          },
        }),
      };
    } else {
      next = {
        version: CACHE_VERSION,
        scope,
        dashboards: pruneDashboardEntries({
          [dashboardId]: {
            updatedAt: Date.now(),
            widgets: minimized,
          },
        }),
      };
    }

    localStorage.setItem(
      DASHBOARD_SKELETON_CACHE_STORAGE_KEY,
      JSON.stringify(next)
    );
  } catch {
    // Quota or private mode — ignore
  }
}

/**
 * Returns cached widget layout for skeleton rendering, or null if missing / stale / wrong scope.
 */
export function readDashboardSkeletonLayout(
  dashboardId: string | null,
  scope: DashboardSkeletonCacheScope | null
): DashboardSkeletonLayoutWidget[] | null {
  if (!dashboardId || !scope) return null;

  try {
    const raw = localStorage.getItem(DASHBOARD_SKELETON_CACHE_STORAGE_KEY);
    if (!raw) return null;

    const doc = fullCacheSchema.safeParse(JSON.parse(raw));
    if (!doc.success) return null;

    if (
      doc.data.scope.userId !== scope.userId ||
      doc.data.scope.tenantId !== scope.tenantId
    ) {
      return null;
    }

    const prunedDashboards = pruneDashboardEntries(doc.data.dashboards);
    const entry = prunedDashboards[dashboardId];
    if (!entry) return null;

    if (Date.now() - entry.updatedAt > MAX_ENTRY_AGE_MS) {
      return null;
    }

    return entry.widgets;
  } catch {
    return null;
  }
}

/**
 * Persists TTL/LRU prune when the normalized cache body changes. Intended for `useEffect`
 * (not render / `useMemo`) because it writes `localStorage`.
 */
export function maintainDashboardSkeletonCache(
  scope: DashboardSkeletonCacheScope | null
): void {
  if (!scope) return;
  try {
    const raw = localStorage.getItem(DASHBOARD_SKELETON_CACHE_STORAGE_KEY);
    if (!raw) return;

    const doc = fullCacheSchema.safeParse(JSON.parse(raw));
    if (!doc.success) return;

    if (
      doc.data.scope.userId !== scope.userId ||
      doc.data.scope.tenantId !== scope.tenantId
    ) {
      return;
    }

    rewriteStorageIfDashboardsPruned(doc.data);
  } catch {
    // Quota or corrupt JSON — ignore
  }
}

export function clearDashboardSkeletonCache(): void {
  try {
    localStorage.removeItem(DASHBOARD_SKELETON_CACHE_STORAGE_KEY);
  } catch {
    // ignore
  }
}
